/**
 * Masters → Gaps — master-data completeness against the client's spec.
 * @file src/web/features/admin/components/masters/GapsTab.tsx
 *
 * Tally supplies books, not statutory identity, so a freshly imported client
 * has almost none of the compliance masters filled in. This turns that into a
 * worklist: every field from the master-data sheet, whether it is filled, how
 * badly it is needed, and the tab that fixes it.
 *
 * Ordering is by consequence, not by the sheet's layout — `blocking` first,
 * because those are the only ones that stop a return going out today. Items for
 * modules the client has not enabled are collapsed out of the way rather than
 * shown as permanent failures.
 */

import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, MinusCircle, Wand2, RefreshCw, ArrowRight, Loader2 } from 'lucide-react';
import { adminFetch, adminFetchJson } from '../../lib/admin-api';
import { toast } from '@bloomneo/uikit';
import { Card, ErrBox, Loading, Pill, PrimaryBtn } from './shared';

type Severity = 'blocking' | 'required' | 'recommended' | 'phase2';
type Status = 'ok' | 'gap' | 'na';

interface GapItem {
  code: string; label: string; status: Status; severity: Severity;
  module: string; detail: string; fixTab: string | null; affected?: number; note?: string;
}
interface GapSection { key: string; label: string; items: GapItem[] }
interface GapReport {
  entityId: string; entityName: string; modules: Record<string, boolean>;
  summary: { total: number; ok: number; gaps: number; blocking: number; phase2: number };
  sections: GapSection[];
}

const SEVERITY_RANK: Record<Severity, number> = { blocking: 0, required: 1, recommended: 2, phase2: 3 };

const SEVERITY_META: Record<Severity, { label: string; tone: 'danger' | 'warn' | 'neutral' | 'brand'; blurb: string }> = {
  blocking: { label: 'Blocks filing', tone: 'danger', blurb: 'GSTN or the GSP rejects the return without this.' },
  required: { label: 'Required', tone: 'warn', blurb: 'Needed for a compliance module this client has enabled.' },
  recommended: { label: 'Recommended', tone: 'neutral', blurb: 'Improves accuracy; not a hard stop.' },
  phase2: { label: 'Phase 2', tone: 'brand', blurb: 'Schema exists; activation is a later phase.' },
};

export function GapsTab({ entityId, onGoToTab }: { entityId: string | null; onGoToTab: (tab: string) => void }) {
  const [report, setReport] = useState<GapReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(() => {
    if (!entityId) { setError('Select an entity in the header.'); setLoading(false); return; }
    setLoading(true);
    adminFetchJson<GapReport>('/api/masters/gaps', { query: { entityId } })
      .then((d) => { setReport(d); setError(null); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [entityId]);

  useEffect(load, [load]);

  /** Fill what is derivable (PANs from GSTINs) rather than making a human type it. */
  const autofill = async () => {
    if (!entityId || busy) return;
    setBusy(true);
    try {
      const res = await adminFetch('/api/masters/gaps/autofill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityId }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || 'Autofill failed');
      const bits = [
        d.entityPan ? `entity PAN ${d.entityPan}` : null,
        d.partyPans ? `${d.partyPans} party PAN(s)` : null,
      ].filter(Boolean);
      toast.success(bits.length ? `Derived ${bits.join(' and ')}` : 'Nothing further could be derived');
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading label="Checking master data" />;
  if (error) return <ErrBox msg={error} />;
  if (!report) return null;

  const { summary } = report;
  const pct = summary.total ? Math.round((summary.ok / summary.total) * 100) : 0;

  return (
    <>
      {/* headline */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Complete" value={`${pct}%`} sub={`${summary.ok} of ${summary.total} applicable`} tone="ok" />
        <Stat label="Gaps to fill" value={String(summary.gaps)} sub="across all masters" tone="warn" />
        <Stat label="Blocking filing" value={String(summary.blocking)} sub="GSTN rejects without these" tone={summary.blocking ? 'danger' : 'ok'} />
        <Stat label="Phase 2" value={String(summary.phase2)} sub="schema ready, not activated" tone="neutral" />
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <PrimaryBtn onClick={autofill} busy={busy}>
          <Wand2 className="h-4 w-4" /> Autofill what we can derive
        </PrimaryBtn>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-3.5 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Re-check
        </button>
        <label className="ml-auto inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-ink-600">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          Show completed &amp; not-applicable
        </label>
      </div>

      {summary.blocking === 0 && summary.gaps === 0 && (
        <div className="mb-6 flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50 px-6 py-4 text-sm text-ok-800">
          <CheckCircle2 className="h-4 w-4" /> Every applicable master is filled in for this client.
        </div>
      )}

      {report.sections.map((section) => {
        const visible = section.items
          .filter((i) => (showResolved ? true : i.status === 'gap'))
          .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
        if (!visible.length) return null;

        const gapCount = section.items.filter((i) => i.status === 'gap').length;
        return (
          <Card
            key={section.key}
            title={section.label}
            subtitle={gapCount ? `${gapCount} field(s) still to fill` : 'Complete'}
          >
            <ul className="divide-y divide-ink-100">
              {visible.map((item) => <GapRow key={item.code} item={item} onGoToTab={onGoToTab} />)}
            </ul>
          </Card>
        );
      })}
    </>
  );
}

function GapRow({ item, onGoToTab }: { item: GapItem; onGoToTab: (t: string) => void }) {
  const meta = SEVERITY_META[item.severity];
  const icon = item.status === 'ok'
    ? <CheckCircle2 className="h-4 w-4 text-ok-500" />
    : item.status === 'na'
      ? <MinusCircle className="h-4 w-4 text-ink-300" />
      : <AlertTriangle className={`h-4 w-4 ${item.severity === 'blocking' ? 'text-danger-500' : 'text-warn-500'}`} />;

  return (
    <li className="flex flex-wrap items-start gap-3 px-6 py-4">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-sm font-semibold ${item.status === 'na' ? 'text-ink-400' : 'text-ink-900'}`}>
            {item.label}
          </span>
          {item.status === 'gap' && <Pill tone={meta.tone}>{meta.label}</Pill>}
          <span className="text-[11px] uppercase tracking-wide text-ink-400">{item.module}</span>
        </div>
        <p className={`mt-1 text-sm ${item.status === 'gap' ? 'text-ink-700' : 'text-ink-500'}`}>{item.detail}</p>
        {item.note && item.status === 'gap' && (
          <p className="mt-1 text-[12px] leading-relaxed text-ink-500">{item.note}</p>
        )}
      </div>
      {item.status === 'gap' && item.fixTab && (
        <button
          onClick={() => onGoToTab(item.fixTab!)}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-ink-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-ink-700 transition hover:bg-ink-50"
        >
          Fix <ArrowRight className="h-3 w-3" />
        </button>
      )}
    </li>
  );
}

function Stat({ label, value, sub, tone }: {
  label: string; value: string; sub: string; tone: 'ok' | 'warn' | 'danger' | 'neutral';
}) {
  const ring = {
    ok: 'border-ok-200 bg-ok-50/50',
    warn: 'border-warn-200 bg-warn-50/50',
    danger: 'border-danger-200 bg-danger-50/50',
    neutral: 'border-ink-200 bg-white',
  }[tone];
  const text = {
    ok: 'text-ok-700', warn: 'text-warn-700', danger: 'text-danger-700', neutral: 'text-ink-900',
  }[tone];
  return (
    <div className={`rounded-2xl border p-5 ${ring}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</p>
      <p className={`mt-2 text-3xl font-semibold tabular-nums ${text}`}>{value}</p>
      <p className="mt-1 text-xs text-ink-500">{sub}</p>
    </div>
  );
}

export { Loader2 };
